import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Row, Col, Button, Empty } from 'antd';
import { ThunderboltOutlined, BarChartOutlined, ArrowRightOutlined, DashboardOutlined, LockOutlined, LogoutOutlined } from '@ant-design/icons';
import appSelectionBg from '../assets/app-selection-bg-3.png';
import logo from '../assets/logo.png';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

export default function AppSelectionPage() {
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuth();
  const [currentUser, setCurrentUser] = useState(authUser);

  useEffect(() => {
    api.get('/auth/me/')
      .then(res => {
        setCurrentUser(res.data);
      })
      .catch(err => {
        console.error("Error fetching user profile:", err);
      });
  }, []);

  const apps = [
    {
      id: 'trading',
      title: 'Power Trading Optimization',
      description: 'Advanced analytics for power trading, forecast vs actual analysis, and revenue optimization.',
      icon: <ThunderboltOutlined style={{ fontSize: 26, color: '#0052CC' }} />,
      color: '#0052CC',
      bgColor: '#E6F0FF',
      route: '/',
      active: true
    },
    {
      id: 'bess',
      title: 'BESS Sizing Project',
      description: 'Battery Energy Storage System sizing and optimization tool for renewable energy integration.',
      icon: <BarChartOutlined style={{ fontSize: 26, color: '#0D9488' }} />,
      color: '#0D9488',
      bgColor: '#E6FDF9',
      route: '/bess-sizing',
      active: true
    },
    {
      id: 'dsm_minimization',
      title: 'DSM Minimization',
      description: 'Advanced tool to track 15-min deviations, analyze generation forecasts, and minimize DSM penalties.',
      icon: <DashboardOutlined style={{ fontSize: 26, color: '#D97706' }} />,
      color: '#D97706',
      bgColor: '#FEF3C7',
      route: '/dsm-minimization',
      active: true
    }
  ];

  // Filter apps based on what the user is allowed to see
  const visibleApps = useMemo(() => {
    const userToEvaluate = currentUser || authUser;
    if (!userToEvaluate) return apps;

    const allowed = userToEvaluate.allowed_apps;
    // Superusers see all apps by default unless explicit allowed_apps is configured
    if (userToEvaluate.is_superuser && (!allowed || allowed.length === 0)) {
      return apps;
    }
    if (Array.isArray(allowed)) {
      return apps.filter(app => allowed.includes(app.id));
    }
    return apps;
  }, [currentUser, authUser]);

  const customStyles = `
    .app-selection-container {
      box-sizing: border-box;
      height: 100vh;
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 24px;
      background-color: #f8fafc;
      overflow: hidden;
    }
    .app-selection-container::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: url(${appSelectionBg});
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      opacity: 0.3; /* Added opacity to background image */
      z-index: 1;
    }
    .app-selection-container > div {
      position: relative;
      z-index: 2;
    }
    .enter-btn {
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
    }
    .enter-btn:hover {
      background: #0040B3 !important;
      box-shadow: 0 6px 20px rgba(0, 82, 204, 0.3) !important;
    }
    .enter-btn:hover .anticon {
      transform: translateX(4px);
      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }
  `;

  return (
    <div className="app-selection-container">
      <style>{customStyles}</style>

      {/* Top Header Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 32px',
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        maxWidth: 1400,
        width: '100%',
        margin: '0 auto'
      }}>
        {/* Left Side: Brand Logo and Portal Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <img src={logo} alt="Apraava Energy" style={{ height: '36px', objectFit: 'contain' }} />
          <div style={{ width: '1px', height: '24px', backgroundColor: '#E2E8F0' }} />
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#1B4F72', letterSpacing: '0.3px', fontFamily: 'Inter, sans-serif' }}>
            Applications Portal
          </span>
        </div>

        {/* Right Side: Authenticated User Info */}
        {currentUser && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#1F2937' }}>
                {currentUser.username}
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                {currentUser.email}
              </div>
            </div>
            {/* User Avatar */}
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              backgroundColor: '#1B4F72',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              boxShadow: '0 4px 10px rgba(27, 79, 114, 0.25)',
              fontFamily: 'Inter, sans-serif'
            }}>
              {currentUser.username ? currentUser.username[0] : 'U'}
            </div>
            <Button
              type="text"
              size="small"
              icon={<LogoutOutlined />}
              onClick={() => {
                logout();
                navigate('/login');
              }}
              style={{ color: '#6B7280', marginLeft: 8 }}
            >
              Sign Out
            </Button>
          </div>
        )}
      </div>

      {/* Cards Container */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        width: '100%',
      }}>
        {visibleApps.length === 0 ? (
          <Card
            style={{
              maxWidth: 500,
              width: '100%',
              borderRadius: 20,
              textAlign: 'center',
              padding: '24px',
              boxShadow: '0 16px 36px rgba(0, 0, 0, 0.05)',
              border: '1px solid #E2E8F0',
              background: 'rgba(255, 255, 255, 0.95)'
            }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div style={{ padding: '8px 0' }}>
                  <Title level={4} style={{ color: '#1F2937', marginBottom: 8 }}>
                    No Applications Assigned
                  </Title>
                  <Text type="secondary" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    Your account has not been granted access to any applications yet. Please contact your system administrator.
                  </Text>
                </div>
              }
            >
              <Button
                type="primary"
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                style={{ background: '#0052CC', marginTop: 12 }}
              >
                Sign Out
              </Button>
            </Empty>
          </Card>
        ) : (
          <Row gutter={[32, 32]} justify="center" style={{ maxWidth: 1400, width: '100%', margin: '0 auto' }}>
            {visibleApps.map(app => (
              <Col
                xs={24}
                sm={visibleApps.length === 1 ? 16 : 12}
                md={visibleApps.length === 1 ? 12 : visibleApps.length === 2 ? 10 : 8}
                key={app.id}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
              <Card
                style={{
                  width: '100%',
                  minWidth: 320,
                  minHeight: 340,
                  borderRadius: 24,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
                  opacity: app.active ? 1 : 0.85,
                  cursor: app.active ? 'pointer' : 'default',
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#ffffff'
                }}
                onClick={() => app.active && navigate(app.route)}
                styles={{
                  body: {
                    padding: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    flex: 1
                  }
                }}
              >
                {/* Icon Wrapper */}
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: app.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 32
                }}>
                  {app.icon}
                </div>

                {/* Title */}
                <Title level={3} style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: '#1F2937',
                  marginBottom: 16,
                  marginTop: 0
                }}>
                  {app.title}
                </Title>

                {/* Description */}
                <Text style={{
                  color: '#4B5563',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  marginBottom: 32,
                  display: 'block',
                  flex: 1
                }}>
                  {app.description}
                </Text>

                {/* Bottom Action Area */}
                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-start' }}>
                  {app.active ? (
                    <Button
                      type="primary"
                      shape="round"
                      size="large"
                      className="enter-btn"
                      style={{
                        background: '#0052CC',
                        border: 'none',
                        height: 48,
                        padding: '0 28px',
                        fontSize: '15px',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        boxShadow: '0 4px 12px rgba(0, 82, 204, 0.15)'
                      }}
                    >
                      Enter Application
                    </Button>
                  ) : (
                    <div style={{
                      color: app.color,
                      background: app.bgColor,
                      padding: '8px 18px',
                      borderRadius: 100,
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 36
                    }}>
                      Coming Soon
                      {app.tag}
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          ))}
          </Row>
        )}
      </div>

      {/* Bottom Footer */}
      <div style={{ textAlign: 'center', fontSize: '12px', color: '#4B5563', padding: '10px 0', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
        © {new Date().getFullYear()} Apraava Energy. All rights reserved.
      </div>
    </div >
  );
}
